<template>
  <div class="generic-listing-filter">
    <!-- Desktop Variant -->
    <div class="d-none d-lg-block">
      <div v-if="showCount" class="generic-listing-filter-count-wrap fw-bold fs-eyebrow" aria-live="polite">
        <slot name="generic-filter-count-prefix"></slot>
        <span class="filter-count">{{ results ? results.length : '' }}</span>
        <slot name="generic-filter-count-suffix"></slot>
      </div>
      <template v-for="(group, groupIndex) in filterGroups">
        <div v-if="showGroup(group)" class="mb-3">
          <button v-if="group.id != 'default'" 
                  class="btn btn-link text-black w-100 text-start ps-0 mb-3 border-0 fs-large fw-medium caret-large caret-lg-base" 
                  data-bs-toggle="collapse" 
                  :data-bs-target="'#generic-filter-list-' + group.id" 
                  aria-expanded="true" 
                  :aria-controls="'generic-filter-list-' + group.id">
            {{ group.title }}
          </button>
          <div class="collapse show" :id="'desktop-generic-filter-list-' + group.id">
            <form :id="'desktop-generic-filter-form-' + group.id" :class="'hide-more-container' + (group.showMore?'':' hide-more-items')">
              <div v-for="(filter, index) in group.items" :class="'form-check' + (9 < index ? ' more-item' : '')">
                <input :id="`desktop-generic-filter-${group.id}-${filter.value}`" 
                       class="form-check-input" 
                       type="checkbox" 
                       :value="filter.value" 
                       v-model="currentQuery.filters[group.id]" 
                       :disabled="showCount && filterCount(group.id, filter.value) == 0"
                       @click="onFilterChange(group.id)" />
                <label class="form-check-label text-uppercase" :for="`desktop-generic-filter-${group.id}-${filter.value}`">
                  <slot :name="`generic-filter-label-${group.id.toString().toLowerCase()}-${filter.value.toString().toLowerCase()}`">
                    <i class="fst-normal" v-html="filter.title"></i>
                  </slot>
                  <span v-if="showItemCount" class="ms-1">
                    <slot name="generic-filter-item-count-prefix"></slot>
                    <span class="filter-count">{{ filterCount(group.id, filter.value) }}</span>
                    <slot name="generic-filter-item-count-suffix"></slot>
                  </span>
                </label>
              </div>
              <a @click="group.showMore=!group.showMore" 
                 href="#more" 
                 v-if="9 < group.items.length" 
                 class="mb-2 d-block text-end">
                <slot v-if="!group.showMore" name="generic-filter-show-all"></slot>
                <slot v-if="group.showMore" name="generic-filter-show-less"></slot>
              </a>                  
            </form>
          </div>
        </div>
      </template>
    </div>

    <!-- Mobile Variant -->
    <div v-if="filterGroups.filter((group) => group.items.length).length" class="d-block d-lg-none">
      <button class="btn btn-link text-black px-0 my-3 bg-transparent" 
              data-bs-toggle="offcanvas" 
              data-bs-target="#generic-filter-list-mobile" 
              aria-expanded="true" 
              aria-controls="generic-filter-list-mobile">
        <slot name="generic-filter-mobile-title"></slot>
      </button>
      <div class="offcanvas offcanvas-end w-100" tabindex="-1" id="generic-filter-list-mobile">
        <div class="offcanvas-header justify-content-end">
          <button type="button" class="btn-close mr-4" data-bs-dismiss="offcanvas" aria-label="Close">
            <slot name="offcanvas-close-button"></slot>
          </button>
        </div>
        <div class="offcanvas-body">
          <div class="offcanvas-panel-title">
            <strong class="h3">
              <slot name="generic-filter-offcanvas-title"></slot>
            </strong>
          </div>
          <div class="offcanvas-panel-body pb-0">
            <div v-if="showCount" class="generic-listing-filter-count-wrap fw-bold fs-eyebrow" aria-live="polite">
              <slot name="generic-filter-count-prefix"></slot>
              <span class="filter-count">{{ results ? results.length : '' }}</span>
              <slot name="generic-filter-count-suffix"></slot>
            </div>
            <div v-if="$slots['generic-filter-title']" class="text-uppercase fw-bold my-3">
              <slot name="generic-filter-title"></slot>
            </div>
            <div v-for="(group, groupIndex) in filterGroups">
              <template v-if="showGroup(group)">
                <button v-if="group.id != 'default'" 
                        class="btn btn-link text-black w-100 text-start my-3 border-0 fs-large fw-medium caret-large caret-lg-base" 
                        data-bs-toggle="collapse" 
                        :data-bs-target="'#generic-filter-list-' + group.id" 
                        aria-expanded="true" 
                        :aria-controls="'generic-filter-list-' + group.id">
                  {{ group.title }}
                </button>
                <div class="collapse show" :id="'mobile-generic-filter-list-' + group.id">
                  <form :id="'mobile-generic-filter-form-' + group.id" :class="'hide-more-container' + (group.showMore?'':' hide-more-items')">
                    <div v-for="(filter, index) in group.items" :class="'form-check' + (9 < index ? ' more-item' : '')">
                      <input :id="`mobile-generic-filter-${group.id}-${filter.value}`" 
                             class="form-check-input" 
                             type="checkbox" 
                             :value="filter.value" 
                             v-model="currentQuery.filters[group.id]" 
                             :disabled="showItemCount && filterCount(group.id, filter.value) == 0"
                             @click="onFilterChange(group.id)" />
                      <label class="form-check-label text-uppercase" :for="`mobile-generic-filter-${group.id}-${filter.value}`">
                        <slot :name="`generic-filter-label-${group.id.toString().toLowerCase()}-${filter.value.toString().toLowerCase()}`">
                          <i class="fst-normal" v-html="filter.title"></i>
                        </slot>
                        <span v-if="showItemCount" class="ms-1">
                          <slot name="generic-filter-item-count-prefix"></slot>
                          <span class="filter-count">{{ filterCount(group.id, filter.value) }}</span>
                          <slot name="generic-filter-item-count-suffix"></slot>
                        </span>
                      </label>
                    </div>
                    <a @click="group.showMore=!group.showMore" 
                       href="#more" 
                       v-if="9 < group.items.length" 
                       class="mb-2 d-block text-end">
                      <slot v-if="!group.showMore" name="generic-filter-show-all"></slot>
                      <slot v-if="group.showMore" name="generic-filter-show-less"></slot>
                    </a>
                  </form>
                </div>
              </template>
            </div>
            <div class="offcanvas-panel-footer">
              <button class="btn btn-primary w-100" data-bs-dismiss="offcanvas" aria-label="Close">
                <slot name="generic-filter-apply"></slot>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  export default {
    props: ['hideEmptyGroup', 'filterGroups', 'results', 'currentQuery', 'showItemCount', 'showCount'],
    methods: {
      filterCount(groupId, value) {
        // grab a deep copy for safe modification
        let query = JSON.parse(JSON.stringify(this.currentQuery));
        query.filters[groupId] = [value]; 
        return this.$parent.filterByQuery(query).length
      },
      showFilter(groupId, value) {
        // if doing counting, then hide filter if it returns no items
        return !this.showCount || this.showCount && this.filterCount(groupId, value) > 0;
      },
      showGroup(group) {
        // if don't care, just show it
        if(!this.hideEmptyGroup) return true;

        // if no items, then nothing to show
        if(0 == group.items.length) return false;

        if('model' == group.id)
        {
          // when category and keyword are used, such as on tech doc page
          if(typeof this.currentQuery.category !== 'undefined' && typeof this.currentQuery.keyword !== 'undefined')
          {
            // don't show unless a keyword or category was selected
            if(!this.currentQuery.category && !this.currentQuery.keyword) return false;
          }

          // only show model filter if category selected
          if(Array.isArray(this.currentQuery.filters.category) && 0 == this.currentQuery.filters.category.length) return false;
          else if(typeof this.currentQuery.category !== 'undefined' && !this.currentQuery.category) return false;
        }

        return true;
      },
      onFilterChange(id) {
        this.$emit("filterChange", id);
      }
    },
    emits: ['categoryChange', 'filterChange']
  }
</script>
